/**
 * Mock client — dados coerentes com o discovery 2026-04-17:
 * - 2.672 contatos na base, 1.697 em "Novo Lead"
 * - Pipeline "Esteira Radiologia 2026"
 * - Tags reais: SUMMIT_INTERESSE, 12 Pilares | Turma 3/4/5, WORKSHOP CAFÉS
 * - KPIs de abril/2026: 20 vendas, 35,09% conversão, R$884 ticket médio, R$17.681 total
 */

import type {
  DocsGrowthClient,
  DashboardKpis,
  FunnelStage,
  TagPerformance,
  DailyRevenue,
  Insight,
  StaleLead,
  Tag,
  SalesRepPerformance,
  SdrPerformance,
  ProductPerformance,
  DayDetail,
  DayProductSale,
  DaySellerSale,
  ActivityKpis,
  ActivityDailyPoint,
  ActivityStatus,
  ActivityKind,
  ActivityLog,
  ActivityDayDetail,
  ActivityListPage,
  ConversationKpis,
  ConversationDailyPoint,
  ConversationDayDetail,
  ConversationDayLead,
  CallKpis,
  CallDailyPoint,
  CallDayDetail,
  CallLog,
  CallOutcome,
  SdrFunnelKpis,
  SdrFunnelDailyPoint,
  SdrStageKey,
  SdrStageEntry,
  SdrStageDetail,
  CloserFunnelKpis,
  CloserFunnelDailyPoint,
  CloserStageKey,
  CloserStageEntry,
  CloserStageDetail,
  CloserMeetingOutcome,
} from "./types";

function delay<T>(value: T, ms = 300): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

// Tags do vault (discovery 2026-04-17)
const TAG_SUMMIT: Tag = { id: 3, name: "SUMMIT_INTERESSE", color: "#06B6D4", companyId: 1 };
const TAG_T5: Tag = { id: 1, name: "12 Pilares | Turma 5", color: "#F59E0B", companyId: 1 };
const TAG_T4: Tag = { id: 2, name: "12 Pilares | Turma 4", color: "#8B5CF6", companyId: 1 };
const TAG_T3: Tag = { id: 5, name: "12 Pilares | Turma 3", color: "#EF4444", companyId: 1 };
const TAG_CAFES: Tag = { id: 4, name: "WORKSHOP CAFÉS", color: "#10B981", companyId: 1 };

const STEPS = [
  { name: "Tentativa de Contato", color: "#EAB308" },
  { name: "Reativação", color: "#F97316" },
  { name: "Qualificado", color: "#06B6D4" },
  { name: "Proposta Enviada", color: "#8B5CF6" },
];

const FIRST_NAMES = [
  "Ana", "Bruno", "Carla", "Daniel", "Eduarda", "Fábio", "Gabriela", "Henrique",
  "Isabela", "João", "Larissa", "Marcos", "Natália", "Otávio", "Patrícia", "Rafael",
  "Sofia", "Thiago", "Vanessa", "Wellington", "Beatriz", "Caio", "Débora", "Emerson",
  "Fernanda", "Gustavo", "Helena", "Ícaro", "Júlia", "Kauê", "Letícia", "Murilo",
];
const LAST_NAMES = [
  "Silva", "Santos", "Oliveira", "Souza", "Ferreira", "Almeida", "Costa", "Pereira",
  "Ribeiro", "Martins", "Rocha", "Carvalho", "Gomes", "Barbosa", "Araújo", "Cardoso",
  "Nascimento", "Lima", "Mendes", "Castro", "Pinto", "Teixeira", "Moreira", "Correia",
];
const ASSIGNEES = ["Maria S.", "Ana Vieira", "Carlos Menezes", "Juliana Ramos", "Pedro Alves"];

// PRNG deterministic — fixtures idênticos em cada render
function mulberry32(seed: number) {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildStaleLeads(count: number): StaleLead[] {
  const rnd = mulberry32(42);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rnd() * arr.length)];
  const allTags = [TAG_SUMMIT, TAG_T5, TAG_T4, TAG_T3, TAG_CAFES];

  return Array.from({ length: count }, (_, i) => {
    const step = STEPS[Math.floor(rnd() * STEPS.length)];
    const tagCount = 1 + Math.floor(rnd() * 2);
    const tags: Tag[] = [];
    for (let j = 0; j < tagCount; j++) {
      const t = pick(allTags);
      if (!tags.find((x) => x.id === t.id)) tags.push(t);
    }
    const daysStale = 30 + Math.floor(rnd() * 91); // 30–120
    const lastInteraction = new Date(2026, 3, 17);
    lastInteraction.setDate(lastInteraction.getDate() - daysStale);
    const ddd = 11 + Math.floor(rnd() * 89);
    const numPart = 900000000 + Math.floor(rnd() * 99999999);
    return {
      contactId: 10000 + i,
      name: `${pick(FIRST_NAMES)} ${pick(LAST_NAMES)}`,
      number: `55${ddd}${numPart}`,
      email: rnd() > 0.4 ? `${pick(FIRST_NAMES).toLowerCase()}.${pick(LAST_NAMES).toLowerCase()}@gmail.com` : undefined,
      tags,
      currentStepName: step.name,
      currentStepColor: step.color,
      amountAtRisk: Math.round((500 + rnd() * 4500) * 100) / 100,
      lastInteractionAt: lastInteraction.toISOString(),
      daysStale,
      assignedUserName: pick(ASSIGNEES),
    };
  });
}

const STALE_LEADS = buildStaleLeads(158);

// Vendedores (closers) — ranking interno, totais do período batem com KPIs (20 vendas, R$17.681).
const SELLERS: SalesRepPerformance[] = [
  {
    userId: 101,
    name: "Maria Silva",
    avatarColor: "hsl(38 96% 50%)",
    meetingsHeld: 15,
    salesCount: 8,
    amountInPeriod: 6940,
    amountAccumulated: 28500,
    conversionRate: 53.3,
  },
  {
    userId: 102,
    name: "João Pereira",
    avatarColor: "hsl(259 94% 65%)",
    meetingsHeld: 12,
    salesCount: 5,
    amountInPeriod: 4800,
    amountAccumulated: 19200,
    conversionRate: 41.7,
  },
  {
    userId: 103,
    name: "Carla Ribeiro",
    avatarColor: "hsl(189 94% 43%)",
    meetingsHeld: 11,
    salesCount: 4,
    amountInPeriod: 3580,
    amountAccumulated: 14700,
    conversionRate: 36.4,
  },
  {
    userId: 104,
    name: "Bruno Teixeira",
    avatarColor: "hsl(160 84% 39%)",
    meetingsHeld: 8,
    salesCount: 3,
    amountInPeriod: 2361,
    amountAccumulated: 9850,
    conversionRate: 37.5,
  },
];

// SDRs — prospecção da base de 1.697 leads em "Novo Lead".
const SDRS: SdrPerformance[] = [
  {
    userId: 201,
    name: "Pedro Alves",
    avatarColor: "hsl(38 96% 50%)",
    leadsHandled: 420,
    calls: 680,
    connections: 185,
    appointments: 38,
    connectionRate: 27.2,
  },
  {
    userId: 202,
    name: "Juliana Ramos",
    avatarColor: "hsl(330 81% 60%)",
    leadsHandled: 380,
    calls: 520,
    connections: 160,
    appointments: 32,
    connectionRate: 30.8,
  },
  {
    userId: 203,
    name: "Ricardo Mendes",
    avatarColor: "hsl(259 94% 65%)",
    leadsHandled: 315,
    calls: 480,
    connections: 140,
    appointments: 25,
    connectionRate: 29.2,
  },
  {
    userId: 204,
    name: "Fernanda Lima",
    avatarColor: "hsl(189 94% 43%)",
    leadsHandled: 268,
    calls: 410,
    connections: 118,
    appointments: 19,
    connectionRate: 28.8,
  },
];

// Produtos — mix de exames odontológicos (cliente DocsGrowth = Clínica de Radiologia Odontológica).
const PRODUCTS: ProductPerformance[] = [
  {
    productId: 1,
    name: "Documentação Ortodôntica Completa",
    category: "Documentação",
    unitsSold: 5,
    amountSold: 7000,
    ticket: 1400,
    status: "alta-demanda",
  },
  {
    productId: 2,
    name: "Combo Pré-Implante (TC + Pano)",
    category: "Implantodontia",
    unitsSold: 3,
    amountSold: 4500,
    ticket: 1500,
    status: "ativo",
  },
  {
    productId: 3,
    name: "Tomografia Cone Beam",
    category: "Tomografia",
    unitsSold: 6,
    amountSold: 3120,
    ticket: 520,
    status: "alta-demanda",
  },
  {
    productId: 4,
    name: "Telerradiografia Lateral",
    category: "Cefalometria",
    unitsSold: 2,
    amountSold: 1441,
    ticket: 720.5,
    status: "ativo",
  },
  {
    productId: 5,
    name: "Cefalometria Computadorizada",
    category: "Cefalometria",
    unitsSold: 1,
    amountSold: 900,
    ticket: 900,
    status: "promocao",
  },
  {
    productId: 6,
    name: "Panorâmica Dental",
    category: "Radiografia",
    unitsSold: 3,
    amountSold: 720,
    ticket: 240,
    status: "sazonal",
  },
];

// Catálogo de produtos e vendedores usado pelo modal de detalhamento por dia.
const PRODUCT_CATALOG = PRODUCTS.map((p) => ({
  productId: p.productId,
  productName: p.name,
  category: p.category,
  ticket: p.ticket,
}));
const SELLER_CATALOG = SELLERS.map((s) => ({
  userId: s.userId,
  name: s.name,
  avatarColor: s.avatarColor,
  ytd: s.amountAccumulated - s.amountInPeriod, // acumulado até o início do período
}));

function buildDayDetail(dateISO: string, targetAmount: number): DayDetail {
  const seed = Number(dateISO.replaceAll("-", ""));
  const rnd = mulberry32(seed);

  if (targetAmount <= 0) {
    return {
      date: dateISO,
      totalAmount: 0,
      totalSales: 0,
      products: [],
      sellers: [],
    };
  }

  // Seleciona 1–3 produtos e distribui unidades até aproximar targetAmount.
  const productCount = 1 + Math.floor(rnd() * 3);
  const chosenCatalog = [...PRODUCT_CATALOG]
    .sort(() => rnd() - 0.5)
    .slice(0, productCount);

  let remaining = targetAmount;
  const products: DayProductSale[] = [];
  for (let i = 0; i < chosenCatalog.length; i++) {
    const p = chosenCatalog[i];
    const isLast = i === chosenCatalog.length - 1;
    const maxUnits = Math.max(1, Math.floor(remaining / p.ticket));
    const units = isLast
      ? Math.max(1, maxUnits)
      : Math.max(1, Math.floor(1 + rnd() * Math.min(3, maxUnits)));
    const amountSold = Math.round(units * p.ticket);
    products.push({
      productId: p.productId,
      productName: p.productName,
      category: p.category,
      unitsSold: units,
      ticket: p.ticket,
      amountSold,
    });
    remaining -= amountSold;
  }

  const totalSales = products.reduce((a, p) => a + p.unitsSold, 0);
  const totalAmount = products.reduce((a, p) => a + p.amountSold, 0);

  // Distribui o total do dia entre 1–3 vendedores (respeitando totalAmount).
  const sellerCount = Math.min(SELLER_CATALOG.length, 1 + Math.floor(rnd() * 3));
  const chosenSellers = [...SELLER_CATALOG].sort(() => rnd() - 0.5).slice(0, sellerCount);
  const weights = chosenSellers.map(() => 0.5 + rnd());
  const weightSum = weights.reduce((a, b) => a + b, 0);
  let distributed = 0;
  const sellers: DaySellerSale[] = chosenSellers.map((s, i) => {
    const isLast = i === chosenSellers.length - 1;
    const share = isLast
      ? totalAmount - distributed
      : Math.round((weights[i] / weightSum) * totalAmount);
    distributed += share;
    return {
      userId: s.userId,
      name: s.name,
      avatarColor: s.avatarColor,
      amountInDay: share,
      amountAccumulated: s.ytd + share,
      sales: Math.max(1, Math.round((share / totalAmount) * totalSales)),
    };
  });

  return {
    date: dateISO,
    totalAmount,
    totalSales,
    products,
    sellers,
  };
}

export const mockClient: DocsGrowthClient = {
  async getDashboardKpis({ periodStart, periodEnd }): Promise<DashboardKpis> {
    return delay({
      vendas: 20,
      vendasDeltaPct: 18,
      conversao: 35.09,
      conversaoDeltaPp: 4.2,
      ticketMedio: 884.05,
      ticketMedioDeltaPct: -3,
      valorTotal: 17681,
      valorTotalDeltaPct: 22,
      periodo: { start: periodStart, end: periodEnd },
    });
  },

  async getFunnelStages(): Promise<FunnelStage[]> {
    return delay([
      { stepId: 1, stepName: "Novo Lead", order: 1, color: "#F59E0B", count: 1697, amount: 1987, conversionFromPrevious: null },
      { stepId: 2, stepName: "Tentativa de Contato", order: 2, color: "#EAB308", count: 890, amount: 3177, conversionFromPrevious: 52.4 },
      { stepId: 3, stepName: "Qualificado", order: 3, color: "#06B6D4", count: 310, amount: 6240, conversionFromPrevious: 34.8 },
      { stepId: 4, stepName: "Proposta Enviada", order: 4, color: "#8B5CF6", count: 140, amount: 9867, conversionFromPrevious: 45.1 },
      { stepId: 5, stepName: "Fechado (Venda)", order: 5, color: "#10B981", count: 20, amount: 17681, conversionFromPrevious: 14.3 },
    ]);
  },

  async getTopTags({ limit = 5 }): Promise<TagPerformance[]> {
    return delay(
      [
        { tagId: 1, tagName: "12 Pilares | Turma 5", color: "#F59E0B", rank: 1, conversionRate: 48, totalOrders: 92 },
        { tagId: 2, tagName: "12 Pilares | Turma 4", color: "#8B5CF6", rank: 2, conversionRate: 42, totalOrders: 71 },
        { tagId: 3, tagName: "SUMMIT_INTERESSE", color: "#06B6D4", rank: 3, conversionRate: 31, totalOrders: 215 },
        { tagId: 4, tagName: "WORKSHOP CAFÉS", color: "#10B981", rank: 4, conversionRate: 24, totalOrders: 48 },
        { tagId: 5, tagName: "12 Pilares | Turma 3", color: "#EF4444", rank: 5, conversionRate: 19, totalOrders: 63 },
      ].slice(0, limit)
    );
  },

  async getDailyRevenue({ periodStart, periodEnd }): Promise<DailyRevenue[]> {
    // Abril 2026 + overlay de Março/2026 (referência)
    const aprValues = [2000, 2950, 100, 80, 60, 90, 250, 3800, 4700, 50, 40, 80, 60, 1950, 1850, 30, 20];
    const marValues = [1800, 2400, 80, 60, 40, 70, 190, 3100, 3900, 40, 30, 60, 50, 1500, 1400, 20, 10];

    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    const days = Math.min(
      aprValues.length,
      Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1)
    );

    return delay(
      Array.from({ length: days }, (_, i) => {
        const d = new Date(start);
        d.setDate(d.getDate() + i);
        return {
          date: d.toISOString().slice(0, 10),
          amount: aprValues[i] ?? 0,
          amountReference: marValues[i] ?? 0,
        };
      })
    );
  },

  async getInsights(): Promise<Insight[]> {
    return delay([
      {
        kind: "leads-parados",
        heading: "Leads Parados",
        value: "158",
        body: "em reativação há mais de 30 dias · R$ 3.177 em oportunidades estagnadas",
        accentColor: "warning",
        linkTo: "leads-parados",
      },
      {
        kind: "tempo-medio",
        heading: "Tempo Médio no Funil",
        value: "14 dias",
        body: "do primeiro contato ao fechamento · 22% melhor que março",
        accentColor: "info",
      },
      {
        kind: "top-performer",
        heading: "Top Performer",
        value: "Maria S.",
        body: "8 vendas em abril · R$ 6.940 · ticket médio 12% acima da equipe",
        accentColor: "success",
        linkTo: "top-performer",
      },
    ]);
  },

  async getStaleLeads(params): Promise<{ data: StaleLead[]; total: number }> {
    const offset = params?.offset ?? 0;
    const limit = params?.limit ?? STALE_LEADS.length;
    return delay({
      data: STALE_LEADS.slice(offset, offset + limit),
      total: STALE_LEADS.length,
    });
  },

  async getSalesReps(): Promise<SalesRepPerformance[]> {
    return delay(SELLERS);
  },

  async getSdrs(): Promise<SdrPerformance[]> {
    return delay(SDRS);
  },

  async getProducts(): Promise<ProductPerformance[]> {
    return delay(PRODUCTS);
  },

  async getDayDetail({ date }): Promise<DayDetail> {
    const aprValues = [2000, 2950, 100, 80, 60, 90, 250, 3800, 4700, 50, 40, 80, 60, 1950, 1850, 30, 20];
    const dayIndex = Math.max(0, new Date(date).getDate() - 1);
    const target = aprValues[dayIndex] ?? 0;
    return delay(buildDayDetail(date, target));
  },

  // --- Atividades ----------------------------------------------------------
  async getActivityKpis({ periodStart, periodEnd }): Promise<ActivityKpis> {
    const days = daysBetween(periodStart, periodEnd);
    const scheduled = Math.round(days * 4.2);
    const completed = Math.round(scheduled * 0.78);
    const missed = Math.round(scheduled * 0.12);
    const onTime = scheduled - completed - missed;
    return delay({
      scheduled,
      completed,
      missed,
      onTime: Math.max(0, onTime),
      scheduledDeltaPct: 14,
      completedDeltaPct: 21,
    });
  },

  async getActivityDaily({ periodStart, periodEnd }): Promise<ActivityDailyPoint[]> {
    return delay(buildSeries(periodStart, periodEnd, 710, (rnd) => {
      const scheduled = 3 + Math.floor(rnd() * 6);
      const completed = Math.max(0, scheduled - Math.floor(rnd() * 3));
      const missed = Math.max(0, scheduled - completed - Math.floor(rnd() * 2));
      const onTime = Math.max(0, completed - Math.floor(rnd() * 2));
      return { scheduled, completed, missed, onTime };
    }));
  },

  async getActivityDayDetail({ date }): Promise<ActivityDayDetail> {
    return delay(buildActivityDayDetail(date));
  },

  async getActivitiesByStatus({ status, periodStart, periodEnd, limit = 50, offset = 0 }): Promise<ActivityListPage> {
    // Agrega todos os dias do período → filtra por status → pagina.
    const start = new Date(periodStart);
    const days = daysBetween(periodStart, periodEnd);
    const all: ActivityLog[] = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      all.push(...buildActivityDayDetail(iso).activities.filter((a) => a.status === status));
    }
    // Ordena: pendentes por data crescente (urgente primeiro), feitas decrescente.
    all.sort((a, b) =>
      status === "scheduled"
        ? a.scheduledAt.localeCompare(b.scheduledAt)
        : b.scheduledAt.localeCompare(a.scheduledAt)
    );
    return delay({
      data: all.slice(offset, offset + limit),
      total: all.length,
    });
  },

  // --- Conversas -----------------------------------------------------------
  async getConversationKpis({ periodStart, periodEnd }): Promise<ConversationKpis> {
    const days = daysBetween(periodStart, periodEnd);
    const total = Math.round(days * 23);
    const newContacts = Math.round(days * 9);
    const answered = Math.round(total * 0.82);
    const answerRate = total ? Math.round((answered / total) * 1000) / 10 : 0;
    return delay({
      total,
      newContacts,
      answered,
      answerRate,
      avgResponseMinutes: 7.4,
      totalDeltaPct: 11,
      newContactsDeltaPct: 18,
      answeredDeltaPct: 14,
      avgResponseDeltaPct: -18, // queda em min = positivo
    });
  },

  async getConversationDaily({ periodStart, periodEnd }): Promise<ConversationDailyPoint[]> {
    return delay(buildSeries(periodStart, periodEnd, 311, (rnd) => {
      const conversations = 12 + Math.floor(rnd() * 35);
      const newContacts = Math.max(0, Math.floor(conversations * (0.25 + rnd() * 0.25)));
      const answered = Math.max(0, Math.floor(conversations * (0.7 + rnd() * 0.22)));
      return { conversations, newContacts, answered };
    }));
  },

  async getConversationDayDetail({ date }): Promise<ConversationDayDetail> {
    return delay(buildConversationDayDetail(date));
  },

  // --- Ligações ------------------------------------------------------------
  async getCallKpis({ periodStart, periodEnd }): Promise<CallKpis> {
    const days = daysBetween(periodStart, periodEnd);
    const total = Math.round(days * 18);
    const answered = Math.round(total * 0.44);
    const connections = Math.round(answered * 0.62);
    const uniqueContacts = Math.round(total * 0.71);
    return delay({
      total,
      uniqueContacts,
      answered,
      connections,
      answerRate: total ? Math.round((answered / total) * 1000) / 10 : 0,
    });
  },

  async getCallDaily({ periodStart, periodEnd }): Promise<CallDailyPoint[]> {
    return delay(buildSeries(periodStart, periodEnd, 917, (rnd) => {
      const total = 10 + Math.floor(rnd() * 20);
      const answered = Math.max(1, Math.floor(total * (0.35 + rnd() * 0.25)));
      const connections = Math.max(0, Math.floor(answered * (0.45 + rnd() * 0.3)));
      const uniqueContacts = Math.max(1, Math.floor(total * (0.6 + rnd() * 0.25)));
      return { total, answered, connections, uniqueContacts };
    }));
  },

  async getCallDayDetail({ date }): Promise<CallDayDetail> {
    return delay(buildCallDayDetail(date));
  },

  // --- SDR Funnel ----------------------------------------------------------
  async getSdrFunnelKpis({ periodStart, periodEnd }): Promise<SdrFunnelKpis> {
    const days = daysBetween(periodStart, periodEnd);
    const leads = Math.round(days * 3.5); // ~59 em 17 dias
    const connections = Math.round(leads * 0.41);
    const meetingsScheduled = Math.round(connections * 0.52);
    const meetingsHeld = Math.round(meetingsScheduled * 0.78);
    const pct = (a: number, b: number) => (b ? Math.round((a / b) * 1000) / 10 : 0);
    return delay({
      leads,
      connections,
      meetingsScheduled,
      meetingsHeld,
      leadsToConnectionsRate: pct(connections, leads),
      connectionsToScheduledRate: pct(meetingsScheduled, connections),
      scheduledToHeldRate: pct(meetingsHeld, meetingsScheduled),
    });
  },

  async getSdrFunnelDaily({ periodStart, periodEnd }): Promise<SdrFunnelDailyPoint[]> {
    return delay(buildSeries(periodStart, periodEnd, 4281, (rnd, i) => {
      const spike = i === 6 ? 22 : 0; // mimetiza pico do dia 7 visto no CRM
      const leads = spike + 2 + Math.floor(rnd() * 8);
      const connections = Math.floor(leads * (0.3 + rnd() * 0.2));
      const meetingsScheduled = Math.floor(connections * (0.4 + rnd() * 0.3));
      const meetingsHeld = Math.floor(meetingsScheduled * (0.7 + rnd() * 0.25));
      return { leads, connections, meetingsScheduled, meetingsHeld };
    }));
  },

  async getSdrStageDetail({ stage, periodStart, periodEnd }): Promise<SdrStageDetail> {
    return delay(buildSdrStageDetail(stage, periodStart, periodEnd));
  },

  // --- Closer Funnel -------------------------------------------------------
  async getCloserFunnelKpis({ periodStart, periodEnd }): Promise<CloserFunnelKpis> {
    // Números oficiais do discovery: 20 vendas, R$17.681 no mês.
    const days = daysBetween(periodStart, periodEnd);
    const scale = Math.min(1, days / 17);
    const meetingsHeld = Math.round(57 * scale);
    const meetingsScheduled = Math.round(meetingsHeld / 0.82);
    const sales = Math.round(20 * scale);
    const valueGenerated = Math.round(17681 * scale);
    const pct = (a: number, b: number) => (b ? Math.round((a / b) * 1000) / 10 : 0);
    return delay({
      meetingsScheduled,
      meetingsHeld,
      sales,
      valueGenerated,
      scheduledToHeldRate: pct(meetingsHeld, meetingsScheduled),
      heldToSalesRate: pct(sales, meetingsHeld),
    });
  },

  async getCloserFunnelDaily({ periodStart, periodEnd }): Promise<CloserFunnelDailyPoint[]> {
    const aprSalesValues = [3, 4, 0, 0, 0, 0, 6, 3, 2, 0, 0, 0, 0, 2, 1, 0, 0];
    return delay(buildSeries(periodStart, periodEnd, 7712, (rnd, i) => {
      const sales = aprSalesValues[i] ?? Math.floor(rnd() * 3);
      const meetingsScheduled = sales + 1 + Math.floor(rnd() * 2);
      const meetingsHeld = Math.max(sales, meetingsScheduled - Math.floor(rnd() * 2));
      return { meetingsScheduled, meetingsHeld, sales };
    }));
  },

  async getCloserStageDetail({ stage, periodStart, periodEnd }): Promise<CloserStageDetail> {
    return delay(buildCloserStageDetail(stage, periodStart, periodEnd));
  },
};

// Catálogo de cores e atendentes reutilizado em múltiplos mocks ------------
const CONV_ASSIGNEES = ["Maria S.", "Ana Vieira", "Carlos Menezes", "Juliana Ramos", "Pedro Alves"];
const CONV_PREVIEWS = [
  "Oi, gostaria de saber valores da panorâmica",
  "Vocês atendem convênio?",
  "Posso remarcar o exame?",
  "Recebi o orçamento, vou me decidir",
  "Precisa de pedido médico?",
  "Obrigado! Até amanhã",
  "Qual o horário de funcionamento?",
  "Alguém pode me ligar?",
  "Já confirmaram meu horário?",
  "Pode enviar o endereço?",
];
const AVATAR_COLORS = [
  "hsl(38 96% 50%)",
  "hsl(259 94% 65%)",
  "hsl(189 94% 43%)",
  "hsl(160 84% 39%)",
  "hsl(330 81% 60%)",
  "hsl(24 95% 53%)",
];

function buildConversationDayDetail(dateISO: string): ConversationDayDetail {
  const seed = Number(dateISO.replaceAll("-", "")) ^ 0x7f3a;
  const rnd = mulberry32(seed);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rnd() * arr.length)];

  const leadCount = 8 + Math.floor(rnd() * 18); // 8–25 leads/dia

  const leads: ConversationDayLead[] = Array.from({ length: leadCount }, (_, i) => {
    const first = pick(FIRST_NAMES);
    const last = pick(LAST_NAMES);
    const ddd = 11 + Math.floor(rnd() * 89);
    const numPart = 900000000 + Math.floor(rnd() * 99999999);
    const inbound = 1 + Math.floor(rnd() * 7);
    const outbound = Math.max(0, inbound - Math.floor(rnd() * 2));
    const isNewContact = rnd() > 0.55;
    const avgResponseMinutes = Math.round((2 + rnd() * 22) * 10) / 10;

    const day = new Date(dateISO);
    const hour = 8 + Math.floor(rnd() * 12);
    const minute = Math.floor(rnd() * 60);
    day.setHours(hour, minute, 0, 0);

    return {
      contactId: seed + i,
      name: `${first} ${last}`,
      number: `55${ddd}${numPart}`,
      avatarColor: pick(AVATAR_COLORS),
      messageCount: inbound + outbound,
      inboundMessages: inbound,
      avgResponseMinutes,
      isNewContact,
      lastMessageAt: day.toISOString(),
      lastMessagePreview: pick(CONV_PREVIEWS),
      assignedUserName: pick(CONV_ASSIGNEES),
    };
  });

  // Ordenar por mais recente primeiro
  leads.sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));

  const totalMessages = leads.reduce((a, l) => a + l.messageCount, 0);
  const newContacts = leads.filter((l) => l.isNewContact).length;
  const answered = leads.filter((l) => l.messageCount > l.inboundMessages).length;

  return {
    date: dateISO,
    totalMessages,
    totalLeads: leads.length,
    newContacts,
    answered,
    leads,
  };
}

const CALL_OUTCOMES: { outcome: CallOutcome; weight: number }[] = [
  { outcome: "connected", weight: 22 },
  { outcome: "answered", weight: 18 },
  { outcome: "voicemail", weight: 12 },
  { outcome: "no-answer", weight: 40 },
  { outcome: "failed", weight: 8 },
];

function pickWeightedOutcome(rnd: () => number): CallOutcome {
  const total = CALL_OUTCOMES.reduce((a, o) => a + o.weight, 0);
  let roll = rnd() * total;
  for (const o of CALL_OUTCOMES) {
    roll -= o.weight;
    if (roll <= 0) return o.outcome;
  }
  return "no-answer";
}

function buildCallDayDetail(dateISO: string): CallDayDetail {
  const seed = Number(dateISO.replaceAll("-", "")) ^ 0x4d2a;
  const rnd = mulberry32(seed);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rnd() * arr.length)];

  const callCount = 14 + Math.floor(rnd() * 22); // 14–35 ligações/dia
  const sdrNames = SDRS.map((s) => s.name);
  const tagPool = [TAG_SUMMIT, TAG_T5, TAG_T4, TAG_T3, TAG_CAFES];

  const calls: CallLog[] = Array.from({ length: callCount }, (_, i) => {
    const outcome = pickWeightedOutcome(rnd);
    const answered = outcome === "connected" || outcome === "answered";
    const connected = outcome === "connected";
    const durationSeconds = !answered
      ? 0
      : connected
        ? 120 + Math.floor(rnd() * 480)
        : 18 + Math.floor(rnd() * 45);
    const nextStepScheduled = connected && rnd() > 0.55;

    const first = pick(FIRST_NAMES);
    const last = pick(LAST_NAMES);
    const ddd = 11 + Math.floor(rnd() * 89);
    const numPart = 900000000 + Math.floor(rnd() * 99999999);

    const startHour = 8 + Math.floor(rnd() * 11);
    const startMin = Math.floor(rnd() * 60);
    const startedAt = new Date(dateISO);
    startedAt.setHours(startHour, startMin, 0, 0);

    const tagCount = 1 + Math.floor(rnd() * 2);
    const tags: Tag[] = [];
    for (let j = 0; j < tagCount; j++) {
      const t = pick(tagPool);
      if (!tags.find((x) => x.id === t.id)) tags.push(t);
    }

    return {
      callId: seed + i,
      contactName: `${first} ${last}`,
      contactNumber: `55${ddd}${numPart}`,
      avatarColor: pick(AVATAR_COLORS),
      sdrName: pick(sdrNames),
      startedAt: startedAt.toISOString(),
      durationSeconds,
      outcome,
      nextStepScheduled,
      tags,
    };
  });

  calls.sort((a, b) => b.startedAt.localeCompare(a.startedAt));

  const answeredCalls = calls.filter(
    (c) => c.outcome === "connected" || c.outcome === "answered"
  ).length;
  const connections = calls.filter((c) => c.outcome === "connected").length;
  const appointmentsGenerated = calls.filter((c) => c.nextStepScheduled).length;
  const connectedCalls = calls.filter((c) => c.durationSeconds > 0);
  const avgDurationSeconds = connectedCalls.length
    ? Math.round(connectedCalls.reduce((a, c) => a + c.durationSeconds, 0) / connectedCalls.length)
    : 0;

  return {
    date: dateISO,
    totalCalls: calls.length,
    answeredCalls,
    connections,
    appointmentsGenerated,
    avgDurationSeconds,
    calls,
  };
}

const ACTIVITY_KIND_TITLES: Record<ActivityKind, string[]> = {
  call: [
    "Ligar para confirmar agendamento",
    "Ligação de retorno — orçamento",
    "Ligação de reativação (60d sem contato)",
  ],
  meeting: [
    "Reunião diagnóstica — Elevation",
    "Call de proposta comercial",
    "Reunião de apresentação — clínica",
  ],
  "follow-up": [
    "Follow-up mensagem WhatsApp",
    "Enviar material de apoio",
    "Confirmar recebimento do orçamento",
  ],
  proposal: [
    "Enviar proposta formal",
    "Revisar proposta antes de envio",
    "Proposta de contrato anual",
  ],
  visit: [
    "Visita presencial na clínica",
    "Visita técnica — instalação DS Voice",
    "Inspeção de equipamento radiografia",
  ],
};

const ACTIVITY_NOTES = [
  "Cliente pediu tempo para avaliar",
  "Indicação do médico Dr. Paulo — prioridade alta",
  "Aguardando retorno do RH sobre orçamento",
  "Ticket reaberto após 30 dias",
  "Reagendado por indisponibilidade do paciente",
  "Confirmou interesse, alinhar valores",
  "Quer entender diferencial de cone beam",
];

function buildActivityDayDetail(dateISO: string): ActivityDayDetail {
  const seed = Number(dateISO.replaceAll("-", "")) ^ 0x7a31;
  const rnd = mulberry32(seed);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rnd() * arr.length)];

  const count = 6 + Math.floor(rnd() * 10); // 6–15 atividades/dia
  const kinds: ActivityKind[] = ["call", "meeting", "follow-up", "proposal", "visit"];
  const assignees = [...SELLERS.map((s) => s.name), ...SDRS.map((s) => s.name)];
  const tagPool = [TAG_SUMMIT, TAG_T5, TAG_T4, TAG_T3, TAG_CAFES];

  const activities: ActivityLog[] = Array.from({ length: count }, (_, i) => {
    const kind = pick(kinds);
    // distribuição alinhada aos 4 KPIs (completed ~55%, on-time ~20%, scheduled ~15%, missed ~10%)
    const roll = rnd();
    const status: ActivityStatus =
      roll < 0.55 ? "completed" : roll < 0.75 ? "on-time" : roll < 0.9 ? "scheduled" : "missed";

    const hour = 8 + Math.floor(rnd() * 11);
    const minute = Math.floor(rnd() * 60);
    const scheduled = new Date(dateISO);
    scheduled.setHours(hour, minute, 0, 0);

    const firstName = pick(FIRST_NAMES);
    const lastName = pick(LAST_NAMES);
    const ddd = 11 + Math.floor(rnd() * 89);
    const numPart = 900000000 + Math.floor(rnd() * 99999999);

    const tagCount = rnd() > 0.5 ? 1 : 2;
    const tags: Tag[] = [];
    for (let j = 0; j < tagCount; j++) {
      const t = pick(tagPool);
      if (!tags.find((x) => x.id === t.id)) tags.push(t);
    }

    const durationMinutes =
      status === "completed" || status === "on-time"
        ? 15 + Math.floor(rnd() * 50)
        : undefined;

    const completedAt =
      status === "completed" || status === "on-time"
        ? (() => {
            const c = new Date(scheduled);
            c.setMinutes(c.getMinutes() + (durationMinutes ?? 0) + Math.floor(rnd() * 10));
            return c.toISOString();
          })()
        : undefined;

    return {
      activityId: seed + i,
      title: pick(ACTIVITY_KIND_TITLES[kind]),
      kind,
      status,
      scheduledAt: scheduled.toISOString(),
      completedAt,
      durationMinutes,
      assignedUserName: pick(assignees),
      contactName: `${firstName} ${lastName}`,
      contactNumber: `55${ddd}${numPart}`,
      avatarColor: pick(AVATAR_COLORS),
      notes: rnd() > 0.35 ? pick(ACTIVITY_NOTES) : undefined,
      tags,
    };
  });

  activities.sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));

  const totals = {
    scheduled: activities.filter((a) => a.status === "scheduled").length,
    completed: activities.filter((a) => a.status === "completed").length,
    missed: activities.filter((a) => a.status === "missed").length,
    onTime: activities.filter((a) => a.status === "on-time").length,
  };

  return { date: dateISO, totals, activities };
}

const SDR_STAGE_NOTES: Record<SdrStageKey, string[]> = {
  leads: [
    "Entrou via formulário SUMMIT_INTERESSE",
    "Indicação — paciente ortodôntico anterior",
    "Ads Instagram — campanha clínica",
    "Workshop CAFÉS — convite aceito",
    "Lead morno de maio — reaquecido",
  ],
  connections: [
    "Respondeu WhatsApp em 12min — interesse alto",
    "Confirmou horário disponível na próxima semana",
    "Pediu mais informações sobre diferença entre exames",
    "Mencionou que já cotou em outra clínica",
    "Aceitou agendar demonstração",
  ],
  meetingsScheduled: [
    "Agendado com Dr. via Google Calendar",
    "Reunião confirmada — enviou material prévio",
    "Cliente pediu reagendamento — novo horário marcado",
    "Closer alinhou pauta com SDR antes da reunião",
    "Primeira reunião — closer principal",
  ],
  meetingsHeld: [
    "Reunião executada — proposta a enviar",
    "Closer marcou follow-up para 3 dias",
    "Cliente pediu tempo — rodada de decisão interna",
    "Reunião técnica concluída — próxima é comercial",
    "Demonstração realizada — proposta aceita",
  ],
};

function buildSdrStageDetail(
  stage: SdrStageKey,
  periodStart: string,
  periodEnd: string
): SdrStageDetail {
  // Seed determinístico por estágio + data — mesma janela gera mesma lista.
  const seed =
    Number(periodStart.replaceAll("-", "")) ^
    Number(periodEnd.replaceAll("-", "")) ^
    (stage === "leads" ? 0xa1 : stage === "connections" ? 0xb2 : stage === "meetingsScheduled" ? 0xc3 : 0xd4);
  const rnd = mulberry32(seed);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rnd() * arr.length)];

  // Totais alinhados aproximadamente com os KPIs do SDR funnel
  // para 17 dias (default): leads ~60, connections ~25, scheduled ~13, held ~10.
  const days = daysBetween(periodStart, periodEnd);
  const scale = days / 17;
  const totalsByStage: Record<SdrStageKey, number> = {
    leads: Math.round(60 * scale),
    connections: Math.round(25 * scale),
    meetingsScheduled: Math.round(13 * scale),
    meetingsHeld: Math.round(10 * scale),
  };
  const total = Math.max(1, totalsByStage[stage]);

  const tagPool = [TAG_SUMMIT, TAG_T5, TAG_T4, TAG_T3, TAG_CAFES];
  const sdrNames = SDRS.map((s) => s.name);
  const closerNames = SELLERS.map((s) => s.name);
  const periodEndDate = new Date(periodEnd);

  const entries: SdrStageEntry[] = Array.from({ length: total }, (_, i) => {
    const first = pick(FIRST_NAMES);
    const last = pick(LAST_NAMES);
    const ddd = 11 + Math.floor(rnd() * 89);
    const numPart = 900000000 + Math.floor(rnd() * 99999999);

    const tagCount = 1 + Math.floor(rnd() * 2);
    const tags: Tag[] = [];
    for (let j = 0; j < tagCount; j++) {
      const t = pick(tagPool);
      if (!tags.find((x) => x.id === t.id)) tags.push(t);
    }

    const daysInStage = Math.floor(rnd() * Math.min(days, 14));
    const enteredAt = new Date(periodEndDate);
    enteredAt.setDate(enteredAt.getDate() - daysInStage);

    const base: SdrStageEntry = {
      contactId: seed + i,
      contactName: `${first} ${last}`,
      contactNumber: `55${ddd}${numPart}`,
      avatarColor: pick(AVATAR_COLORS),
      sdrName: pick(sdrNames),
      enteredStageAt: enteredAt.toISOString(),
      daysInStage,
      tags,
      lastNote: pick(SDR_STAGE_NOTES[stage]),
    };

    if (stage === "connections") {
      base.callsMade = 1 + Math.floor(rnd() * 5);
    }
    if (stage === "meetingsScheduled") {
      const meeting = new Date(periodEndDate);
      meeting.setDate(meeting.getDate() + Math.floor(rnd() * 7));
      meeting.setHours(9 + Math.floor(rnd() * 8), Math.floor(rnd() * 60), 0, 0);
      base.scheduledMeetingAt = meeting.toISOString();
    }
    if (stage === "meetingsHeld") {
      base.closerName = pick(closerNames);
    }

    return base;
  });

  // Ordena: leads/connections → mais antigos primeiro (urgência);
  // scheduled → próximos primeiro; held → mais recentes primeiro.
  entries.sort((a, b) => {
    if (stage === "meetingsScheduled") {
      return (a.scheduledMeetingAt ?? "").localeCompare(b.scheduledMeetingAt ?? "");
    }
    if (stage === "meetingsHeld") {
      return b.enteredStageAt.localeCompare(a.enteredStageAt);
    }
    return b.daysInStage - a.daysInStage; // mais tempo parado = mais urgente
  });

  return { stage, total, entries };
}

const CLOSER_STAGE_NOTES: Record<CloserStageKey, string[]> = {
  meetingsScheduled: [
    "Reunião confirmada no Google Calendar",
    "Cliente pediu reagendamento — nova data agendada",
    "Primeira reunião com o closer principal",
    "Demo técnica antes da proposta comercial",
    "Enviado material prévio via WhatsApp",
  ],
  meetingsHeld: [
    "Reunião conduzida — proposta será enviada em 24h",
    "Cliente pediu tempo pra avaliação interna",
    "Reagendou pós-reunião pra trazer a esposa",
    "Proposta alinhada, closer aguarda assinatura",
    "Demo técnica OK — passando pra comercial",
  ],
  sales: [
    "Contrato assinado via DocuSign",
    "Pacote Doc Orto fechado em 3x no cartão",
    "Cliente fechou após indicação do Dr. Paulo",
    "Segunda compra do mesmo cliente",
    "Cross-sell: aceitou cone beam junto com panorâmica",
  ],
  valueGenerated: [
    "Maior ticket do mês — combo pré-implante",
    "Documentação ortodôntica completa",
    "Pacote família — 3 exames",
    "Recorrente — contrato corporativo",
    "Ticket premium — tomografia full",
  ],
};

const CLOSER_PRODUCTS = [
  { name: "Documentação Ortodôntica Completa", ticket: 1400 },
  { name: "Combo Pré-Implante (TC + Pano)", ticket: 1500 },
  { name: "Tomografia Cone Beam", ticket: 520 },
  { name: "Telerradiografia Lateral", ticket: 720 },
  { name: "Cefalometria Computadorizada", ticket: 900 },
  { name: "Panorâmica Dental", ticket: 240 },
];

const CLOSER_OUTCOMES: CloserMeetingOutcome[] = [
  "proposta-enviada",
  "em-negociacao",
  "reagendou",
  "perdido",
];

function buildCloserStageDetail(
  stage: CloserStageKey,
  periodStart: string,
  periodEnd: string
): CloserStageDetail {
  const seed =
    Number(periodStart.replaceAll("-", "")) ^
    Number(periodEnd.replaceAll("-", "")) ^
    (stage === "meetingsScheduled" ? 0xe1 : stage === "meetingsHeld" ? 0xe2 : stage === "sales" ? 0xe3 : 0xe4);
  const rnd = mulberry32(seed);
  const pick = <T,>(arr: T[]) => arr[Math.floor(rnd() * arr.length)];

  const days = daysBetween(periodStart, periodEnd);
  const scale = days / 17;
  // Totais ancorados nos KPIs de abril: scheduled ~62, held ~57, sales=20, value=sum.
  const totalsByStage: Record<CloserStageKey, number> = {
    meetingsScheduled: Math.round(62 * scale),
    meetingsHeld: Math.round(57 * scale),
    sales: Math.round(20 * scale),
    valueGenerated: Math.round(20 * scale), // mesmas vendas, ordenadas por valor
  };
  const total = Math.max(1, totalsByStage[stage]);

  const tagPool = [TAG_SUMMIT, TAG_T5, TAG_T4, TAG_T3, TAG_CAFES];
  const closerNames = SELLERS.map((s) => s.name);
  const sdrNames = SDRS.map((s) => s.name);
  const periodEndDate = new Date(periodEnd);

  const entries: CloserStageEntry[] = Array.from({ length: total }, (_, i) => {
    const first = pick(FIRST_NAMES);
    const last = pick(LAST_NAMES);
    const ddd = 11 + Math.floor(rnd() * 89);
    const numPart = 900000000 + Math.floor(rnd() * 99999999);

    const tagCount = 1 + Math.floor(rnd() * 2);
    const tags: Tag[] = [];
    for (let j = 0; j < tagCount; j++) {
      const t = pick(tagPool);
      if (!tags.find((x) => x.id === t.id)) tags.push(t);
    }

    const daysInStage = Math.floor(rnd() * Math.min(days, 14));
    const enteredAt = new Date(periodEndDate);
    enteredAt.setDate(enteredAt.getDate() - daysInStage);

    const base: CloserStageEntry = {
      contactId: seed + i,
      contactName: `${first} ${last}`,
      contactNumber: `55${ddd}${numPart}`,
      avatarColor: pick(AVATAR_COLORS),
      closerName: pick(closerNames),
      sdrOriginName: pick(sdrNames),
      enteredStageAt: enteredAt.toISOString(),
      daysInStage,
      tags,
      lastNote: pick(CLOSER_STAGE_NOTES[stage]),
    };

    if (stage === "meetingsScheduled") {
      const meeting = new Date(periodEndDate);
      meeting.setDate(meeting.getDate() + Math.floor(rnd() * 7));
      meeting.setHours(9 + Math.floor(rnd() * 8), Math.floor(rnd() * 60), 0, 0);
      base.scheduledMeetingAt = meeting.toISOString();
    }
    if (stage === "meetingsHeld") {
      base.meetingOutcome = pick(CLOSER_OUTCOMES);
    }
    if (stage === "sales" || stage === "valueGenerated") {
      const prod = pick(CLOSER_PRODUCTS);
      base.productName = prod.name;
      // Ticket varia ±15% pra simular descontos/upgrades
      const variation = 0.85 + rnd() * 0.3;
      base.amount = Math.round(prod.ticket * variation);
      base.soldAt = enteredAt.toISOString();
    }

    return base;
  });

  entries.sort((a, b) => {
    if (stage === "valueGenerated") return (b.amount ?? 0) - (a.amount ?? 0);
    if (stage === "sales") return b.enteredStageAt.localeCompare(a.enteredStageAt);
    if (stage === "meetingsScheduled") {
      return (a.scheduledMeetingAt ?? "").localeCompare(b.scheduledMeetingAt ?? "");
    }
    // meetingsHeld — mais recentes primeiro
    return b.enteredStageAt.localeCompare(a.enteredStageAt);
  });

  const totalAmount =
    stage === "sales" || stage === "valueGenerated"
      ? entries.reduce((a, e) => a + (e.amount ?? 0), 0)
      : undefined;

  return { stage, total, totalAmount, entries };
}

// Helpers compartilhados dos novos endpoints ------------------------------
function daysBetween(startIso: string, endIso: string): number {
  const start = new Date(startIso);
  const end = new Date(endIso);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
}

function buildSeries<T extends object>(
  startIso: string,
  endIso: string,
  seed: number,
  factory: (rnd: () => number, dayIndex: number) => T
): Array<T & { date: string }> {
  const days = daysBetween(startIso, endIso);
  const rnd = mulberry32(seed);
  const start = new Date(startIso);
  return Array.from({ length: days }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    return {
      date: d.toISOString().slice(0, 10),
      ...factory(rnd, i),
    };
  });
}
